import React from 'react';
import { Home, Lock, CheckCircle, Box, ArrowUpCircle } from 'lucide-react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useLanguage } from '../../context/LanguageContext';

function HideoutCard({ 
  module, 
  hideoutLevels, 
  hideoutModules, 
  canUpgrade, 
  handleUpgrade 
}) {
  const { t } = useLanguage();
  
  const realLevel = hideoutLevels[module.id] || 0;
  const nextLevel = realLevel + 1;
  const nextLevelData = module.levels.find(l => l.level === nextLevel);
  const isMaxed = realLevel >= module.levels.length;
  const canUpgradeNow = !isMaxed && canUpgrade(module, nextLevel, hideoutLevels);

  const transKey = `hideout_module_${(module.englishName || module.name).replace(/ /g, '_')}`;
  const displayName = t(transKey) && t(transKey) !== transKey ? t(transKey) : module.name;

  return (
    <div className={`flex flex-col h-full bg-[#09090b] border ${canUpgradeNow ? 'border-emerald-500/30' : 'border-zinc-800'} rounded-sm p-5 hover:border-zinc-600 transition-colors shadow-xl`}>
      <div className="flex items-center gap-4 mb-6">
        <div className="relative">
          {module.imageLink ? (
            <LazyLoadImage src={module.imageLink} alt={module.name} effect="blur" className="w-14 h-14 rounded-md bg-black object-cover border border-zinc-700" />
          ) : (
            <div className="w-14 h-14 bg-zinc-900 rounded-md flex items-center justify-center text-zinc-500 border border-zinc-700">
              <Home className="w-6 h-6" />
            </div>
          )}
          <div className="absolute -bottom-2 -right-2 bg-black border border-zinc-700 text-xs font-bold px-1.5 py-0.5 rounded-sm text-zinc-300">
            L{realLevel}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold text-white uppercase tracking-wider truncate">{displayName}</h3>
          <div className="flex items-center gap-2 text-[10px] font-mono text-zinc-500 mt-1">
            <span>MAX: {module.levels.length}</span>
            <span className="w-1 h-1 rounded-full bg-zinc-700"></span>
            <span className={canUpgradeNow ? "text-emerald-500" : "text-zinc-500"}>
              {isMaxed ? (t('hideout_status_completed') || 'Completed') : canUpgradeNow ? (t('hideout_status_ready') || 'Ready') : (t('hideout_status_lock') || 'Locked')}
            </span>
          </div>
        </div>
      </div>

      {!isMaxed && nextLevelData && (
        <div className="mb-6 flex-1 space-y-3">
          <div className="flex items-center justify-between border-b border-zinc-800 pb-2 mb-3">
            <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">
              {t('hideout_req_title') || 'Requirements'} (Lvl {nextLevel})
            </span>
          </div>

          {nextLevelData.stationLevelRequirements?.map((req, idx) => {
            const reqStation = hideoutModules.find(m => m.id === req.station.id);
            const reqName = reqStation ? (t(`hideout_module_${reqStation.englishName.replace(/ /g, '_')}`) || reqStation.name) : req.station.name;
            const isMet = (hideoutLevels[reqStation?.id] || 0) >= req.level;
            return (
              <div key={`req-${idx}`} className={`border rounded-sm px-3 py-2 flex items-center justify-between ${isMet ? 'bg-zinc-900/50 border-zinc-800' : 'bg-red-500/10 border-red-500/30'}`}>
                <span className={`text-xs font-bold flex items-center gap-2 ${isMet ? 'text-zinc-400' : 'text-red-400'}`}>
                  <Lock className="w-3 h-3" /> {reqName}
                </span>
                <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm ${isMet ? 'text-zinc-500' : 'text-red-500 bg-red-900/20'}`}>
                  Lvl {req.level}
                </span>
              </div>
            );
          })}

          {nextLevelData.itemRequirements?.map((item, idx) => (
            <div key={`item-${idx}`} className="flex items-center justify-between bg-zinc-900/50 rounded-sm px-3 py-2 border border-zinc-800 hover:border-zinc-600 transition-colors">
              <div className="flex items-center gap-3 min-w-0">
                {item.item.iconLink ? (
                  <LazyLoadImage src={item.item.iconLink} effect="blur" className="w-6 h-6 object-contain" />
                ) : (
                  <Box className="w-4 h-4 text-zinc-600" />
                )}
                <span className="text-xs text-zinc-300 truncate font-medium">{item.item.shortName || item.item.name}</span>
              </div>
              <span className="text-emerald-500 font-mono font-bold text-xs bg-emerald-900/10 px-1.5 py-0.5 rounded-sm border border-emerald-900/20">
                x{item.count}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="mt-auto pt-4 border-t border-zinc-800">
        {isMaxed ? (
          <div className="w-full bg-yellow-500/10 border border-yellow-500/30 text-yellow-500 py-3 rounded-sm font-bold text-xs text-center flex items-center justify-center gap-2 uppercase tracking-wide">
            <CheckCircle className="w-4 h-4" /> {t('hideout_status_max') || 'Max Level'}
          </div>
        ) : canUpgradeNow ? (
          <button onClick={() => handleUpgrade(module.id, nextLevel)} className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-3 rounded-sm font-bold text-xs transition-all shadow-[0_0_15px_rgba(16,185,129,0.3)] flex items-center justify-center gap-2 uppercase tracking-wide border border-emerald-500 group">
            <ArrowUpCircle className="w-4 h-4 group-hover:-translate-y-0.5 transition-transform" /> {t('upgrade_button') || 'Upgrade'}
          </button>
        ) : (
          <button disabled className="w-full bg-zinc-800 text-zinc-500 py-3 rounded-sm font-bold text-xs cursor-not-allowed flex items-center justify-center gap-2 border border-zinc-700 uppercase tracking-wide opacity-70">
            <Lock className="w-4 h-4" /> {t('hideout_status_lock') || 'Locked'}
          </button>
        )}
      </div>
    </div>
  );
}

export default HideoutCard;